import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, InputAdornment, LinearProgress,
  Paper, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import searchFill from '@iconify/icons-eva/search-fill';
import Page from '../Page';
import {
  getCampaign, listCampaignQueue, getCampaignQueueStats,
  approveQueueEntry, rejectQueueEntry, retryQueueEntry, updateQueueEntryMessages,
  getMySignature,
} from '../../utils/outreachApi';

// ── status / score helpers ────────────────────────────────────────────────────

const STATUS_META = {
  PENDING:      { label: 'Pending',      color: '#637381', bg: '#F4F6F8' },
  ENRICHING:    { label: 'Enriching',    color: '#1890FF', bg: '#E8F4FF' },
  SCORING:      { label: 'Scoring',      color: '#1890FF', bg: '#E8F4FF' },
  GENERATING:   { label: 'Generating',   color: '#1890FF', bg: '#E8F4FF' },
  REVIEW:       { label: 'Review',       color: '#B76E00', bg: '#FFF7CD' },
  APPROVED:     { label: 'Approved',     color: '#229A16', bg: '#E9FCD4' },
  REJECTED:     { label: 'Rejected',     color: '#B72136', bg: '#FFE7D9' },
  DISQUALIFIED: { label: 'Disqualified', color: '#637381', bg: '#F4F6F8' },
  ENROLLED:     { label: 'Enrolled',     color: '#006C9C', bg: '#CAFDF5' },
  FAILED:       { label: 'Failed',       color: '#B72136', bg: '#FFE7D9' },
};

const SCORE_META = {
  QUALIFIED:    { label: 'Qualified',    color: '#229A16', bg: '#E9FCD4' },
  REVIEW:       { label: 'Needs review', color: '#B76E00', bg: '#FFF7CD' },
  DISQUALIFIED: { label: 'Disqualified', color: '#637381', bg: '#F4F6F8' },
};

function StatusChipSmall({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#637381', bg: '#F4F6F8' };
  return (
    <Chip label={m.label} size="small"
      sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700, fontSize: '0.68rem', height: 20, borderRadius: 1 }} />
  );
}

function ScoreBadge({ result }) {
  if (!result) return null;
  const m = SCORE_META[result] || { label: result, color: '#637381', bg: '#F4F6F8' };
  return (
    <Chip label={m.label} size="small"
      sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700, fontSize: '0.68rem', height: 20, borderRadius: 1 }} />
  );
}

// ── collapsible section helper ───────────────────────────────────────────────

function CollapsibleSection({ title, summary, defaultOpen = false, count, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5,
          userSelect: 'none',
          '&:hover .toggle-title': { color: 'primary.main' },
        }}
      >
        <Icon icon={open ? arrowIosDownwardFill : arrowIosForwardFill} width={16} />
        <Typography
          className="toggle-title"
          variant="subtitle2"
          sx={{ fontWeight: 700, ml: 0.5, transition: 'color 0.15s' }}
        >
          {title}
        </Typography>
        {count != null && (
          <Chip
            label={count}
            size="small"
            sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
          />
        )}
        {summary && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }} noWrap>
            {summary}
          </Typography>
        )}
      </Box>
      {open && <Box sx={{ mt: 1 }}>{children}</Box>}
    </Box>
  );
}

// ── post card ────────────────────────────────────────────────────────────────

function PostCard({ post, kind }) {
  const date = post.date ? new Date(post.date).toLocaleDateString() : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
        <Chip
          label={kind}
          size="small"
          color={kind === 'Founder' ? 'primary' : 'default'}
          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
        />
        {date && (
          <Typography variant="caption" color="text.secondary">{date}</Typography>
        )}
        {(post.reactions != null || post.comments != null) && (
          <Typography variant="caption" color="text.secondary">
            {post.reactions ?? 0} ♥ · {post.comments ?? 0} 💬
          </Typography>
        )}
        {post.share_url && (
          <Typography variant="caption" component="a" href={post.share_url}
            target="_blank" rel="noopener" sx={{ color: 'primary.main', ml: 'auto' }}>
            View on LinkedIn ↗
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4, color: 'text.primary' }}>
        {(post.text || '').slice(0, 600)}
        {post.text && post.text.length > 600 ? '…' : ''}
      </Typography>
    </Paper>
  );
}

// ── message template defs ─────────────────────────────────────────────────────

const MSG_DEFS = [
  { key: 'email1',          label: 'Cold Intro',            hasSubject: true,  channel: 'email',    day: 'Day 0',  step: 1 },
  { key: 'linkedin_invite', label: 'LinkedIn Connect',      hasSubject: false, channel: 'linkedin', day: 'Day 1',  step: 2 },
  { key: 'email_followup',  label: 'Soft Follow-up',        hasSubject: true,  channel: 'email',    day: 'Day 7',  step: 3 },
];

const STEP_COLORS = {
  email:    { bg: '#E8F4FF', color: '#1890FF', label: 'Email' },
  linkedin: { bg: '#E8F8FF', color: '#0A66C2', label: 'LinkedIn' },
};

// Mirror of backend `_compose_email_body` — produces the EXACT HTML that will be sent.
// Backend reference: outreach/tasks.py::_compose_email_body
function textToHtml(text) {
  if (!text) return '';
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Double-newline split → paragraphs, single newline → <br>, filter empty paragraphs
  return esc.split('\n\n')
    .filter((p) => p.trim())
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function composeEmailHtml(bodyText, signatureHtml) {
  const bodyHtml = textToHtml(bodyText);
  if (!signatureHtml) return bodyHtml;
  // Backend trusts signature HTML when it contains tags (the rendered TGC template).
  const looksLikeHtml = signatureHtml.includes('<') && signatureHtml.includes('>');
  const sigHtml = looksLikeHtml ? signatureHtml : textToHtml(signatureHtml);
  return (
    bodyHtml +
    '<div style="margin-top:24px;padding-top:12px;border-top:1px solid #e6e8eb;">' +
    sigHtml +
    '</div>'
  );
}

// ── DetailPanel ───────────────────────────────────────────────────────────────

function DetailPanel({ entry, msgs, msgsDirty, notes, setNotes, acting, onMsgChange, onSaveMessages, onApprove, onReject, onRetry }) {
  const canApprove = ['REVIEW', 'REJECTED'].includes(entry.status);
  const canReject  = !['ENROLLED', 'REJECTED'].includes(entry.status);
  const canRetry   = entry.status === 'FAILED';
  const inPipeline = ['PENDING', 'ENRICHING', 'SCORING', 'GENERATING'].includes(entry.status);
  const hasMessages = Object.values(msgs).some(m => m?.body);

  // Preview modal state
  const [previewKey, setPreviewKey] = useState(null);

  // Signature is fully auto-derived from the BA's name. No form, no fields,
  // no save flow. The preview shows the signature rendered with the logged-in
  // user's name; at actual send time the backend uses the sending BA's name.
  const [sigHtml, setSigHtml] = useState('');
  const [signatureMeta, setSignatureMeta] = useState({ pm_name: '', email: null, has_account: null, loaded: false });

  useEffect(() => {
    getMySignature()
      .then((r) => {
        setSigHtml(r.data?.signature_html || '');
        setSignatureMeta({
          pm_name:     r.data?.pm_name || '',
          email:       r.data?.email || null,
          has_account: !!r.data?.has_account,
          loaded:      true,
        });
      })
      .catch(() => setSignatureMeta({ pm_name: '', email: null, has_account: false, loaded: true }));
  }, []);

  const previewDef = previewKey ? MSG_DEFS.find((d) => d.key === previewKey) : null;
  const previewMsg = previewKey ? (msgs[previewKey] || {}) : null;

  return (
    <Stack spacing={2.5}>

      {/* company header */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{entry.company_name}</Typography>
          <StatusChipSmall status={entry.status} />
          {entry.scoring_result && <ScoreBadge result={entry.scoring_result} />}
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {entry.company_country  && <Typography variant="caption" color="text.secondary">{entry.company_country}</Typography>}
          {entry.company_industry && <Typography variant="caption" color="text.secondary">{entry.company_industry}</Typography>}
          {entry.company_employees && <Typography variant="caption" color="text.secondary">{entry.company_employees} employees</Typography>}
          {entry.company_website  && (
            <Typography variant="caption" component="a" href={entry.company_website}
              target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
              {entry.company_website}
            </Typography>
          )}
        </Stack>
        {/* Targeted contact for this row */}
        {(entry.contact_name || entry.contact_title || entry.contact_email) && (() => {
          const v = entry.employment_verification;
          const vMeta = v?.status === 'CURRENT'
            ? { label: '✓ Verified at company', color: 'success' }
            : v?.status === 'POSSIBLY_LEFT'
              ? { label: '⚠ Employer mismatch', color: 'error' }
              : v?.status === 'UNVERIFIED'
                ? { label: 'Employer unverified', color: 'default' }
                : null;
          return (
            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" gap={0.5}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                  Targeted contact
                </Typography>
                {vMeta && (
                  <Chip
                    label={vMeta.label}
                    size="small"
                    color={vMeta.color}
                    variant={vMeta.color === 'default' ? 'outlined' : 'filled'}
                    sx={{ height: 20, fontWeight: 700, fontSize: '0.68rem' }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                {entry.contact_name && (
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{entry.contact_name}</Typography>
                )}
                {entry.contact_title && (
                  <Typography variant="caption" color="text.secondary">{entry.contact_title}</Typography>
                )}
                {entry.contact_email && (
                  <Typography variant="caption" component="a" href={`mailto:${entry.contact_email}`}
                    sx={{ color: 'primary.main' }}>
                    {entry.contact_email}
                  </Typography>
                )}
              </Stack>
              {v?.detail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  {v.detail}
                </Typography>
              )}
            </Paper>
          );
        })()}
      </Box>

      {/* Disqualified callout — Tier-1 hard-filter reason + SaaS classifier cross-check */}
      {entry.status === 'DISQUALIFIED' && (() => {
        const verdict = entry.scoring_data?.apollo_saas_verdict;
        const aiReason = entry.scoring_data?.tier1_fail_reason || entry.scoring_reason || 'No specific reason recorded.';
        const preCheckDisagrees = verdict?.verdict === 'SAAS';
        const sourceLabel = verdict?.source === 'apollo' ? 'Apollo (deterministic)'
                          : verdict?.source === 'perplexity' ? 'Perplexity (live web)'
                          : 'Pre-check';
        return (
          <Alert
            severity={preCheckDisagrees ? 'error' : 'warning'}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              AI Disqualified — Tier-1 hard filter
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{aiReason}</Typography>
            {verdict && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: preCheckDisagrees ? 'error.light' : 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>SaaS pre-check:</Typography>
                  <Chip
                    label={`${verdict.verdict}${verdict.confidence ? ` (${verdict.confidence})` : ''}`}
                    size="small"
                    color={verdict.verdict === 'SAAS' ? 'success' : verdict.verdict === 'NOT_SAAS' ? 'default' : 'default'}
                    sx={{ height: 20, fontWeight: 700, fontSize: '0.68rem' }}
                  />
                  <Chip
                    label={sourceLabel}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.68rem' }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {verdict.evidence}
                </Typography>
                {preCheckDisagrees && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: 'error.dark' }}>
                    ⚠ Pre-check confirmed SaaS but the AI disqualified anyway — likely a bad call. Worth a manual override.
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        );
      })()}

      {/* AI analysis card */}
      {(entry.scoring_reason || entry.personalization_hook || entry.primary_gap || entry.tier2_score != null) && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>AI Analysis</Typography>
          <Stack spacing={1.5}>
            {(entry.tier2_score != null || entry.tier3_score != null) && (
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>Tier 2 — growth</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                    {entry.tier2_score ?? '—'}
                    <Typography component="span" variant="caption" color="text.secondary">/5</Typography>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>Tier 3 — gaps</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                    {entry.tier3_score ?? '—'}
                    <Typography component="span" variant="caption" color="text.secondary">/4</Typography>
                  </Typography>
                </Box>
              </Stack>
            )}
            {entry.scoring_reason && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Rationale</Typography>
                <Typography variant="body2">{entry.scoring_reason}</Typography>
              </Box>
            )}
            {entry.personalization_hook && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Personalization hook</Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                  "{entry.personalization_hook}"
                </Typography>
              </Box>
            )}
            {entry.primary_gap && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Primary gap</Typography>
                <Typography variant="body2">{entry.primary_gap}</Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* FAILED error + retry */}
      {entry.status === 'FAILED' && entry.error_message && (
        <Alert severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<Icon icon={refreshFill} />}
                    onClick={onRetry} disabled={acting}>Retry</Button>
          }>
          {entry.error_message}
        </Alert>
      )}

      {/* pipeline in-progress notice */}
      {inPipeline && (
        <Alert severity="info" icon={<CircularProgress size={16} thickness={5} />}>
          Pipeline still running ({entry.status.toLowerCase()}) — messages will appear once generation completes.
        </Alert>
      )}

      {/* Recent LinkedIn posts — collapsible, fed into the personalization hook */}
      {(() => {
        const founderPosts = entry.li_activity_data?.founder_posts || [];
        const companyPosts = entry.li_activity_data?.company_posts || [];
        const total = founderPosts.length + companyPosts.length;
        if (!total) return null;
        return (
          <CollapsibleSection
            title="Recent LinkedIn posts"
            summary="used to ground the personalization hook"
            count={total}
            defaultOpen={false}
          >
            <Stack spacing={1.25}>
              {founderPosts.map((p, i) => (
                <PostCard key={`f-${i}`} post={p} kind="Founder" />
              ))}
              {companyPosts.map((p, i) => (
                <PostCard key={`c-${i}`} post={p} kind="Company" />
              ))}
            </Stack>
          </CollapsibleSection>
        );
      })()}

      {/* generated messages */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Generated Messages</Typography>
          {msgsDirty && (
            <Button size="small" variant="outlined" onClick={onSaveMessages} disabled={acting}>
              Save edits
            </Button>
          )}
        </Stack>

        {!hasMessages && !inPipeline ? (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No messages generated yet.</Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {MSG_DEFS.map(({ key, label, hasSubject, channel, day, step }) => {
              const m = msgs[key] || {};
              if (!m.body && !m.subject) return null;
              const chanMeta = STEP_COLORS[channel] || STEP_COLORS.email;
              return (
                <Paper
                  key={key}
                  variant="outlined"
                  sx={{
                    p: 2, borderRadius: 1.5,
                    borderLeft: '4px solid',
                    borderLeftColor: chanMeta.color,
                  }}
                >
                  {/* Header row: step circle | label + day + channel | preview button */}
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '50%',
                      bgcolor: chanMeta.color, color: 'common.white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: '0.85rem', flexShrink: 0,
                    }}>
                      {step}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                        Step {step} — {label}
                      </Typography>
                      <Stack direction="row" spacing={0.75} mt={0.25} flexWrap="wrap">
                        <Chip
                          label={day}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                        />
                        <Chip
                          label={chanMeta.label}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: chanMeta.bg, color: chanMeta.color,
                          }}
                        />
                      </Stack>
                    </Box>
                    {channel === 'email' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Icon icon={eyeFill} width={14} />}
                        onClick={() => setPreviewKey(key)}
                      >
                        Preview
                      </Button>
                    )}
                  </Stack>

                  {hasSubject && (
                    <TextField
                      size="small" fullWidth label="Subject"
                      value={m.subject || ''}
                      onChange={(e) => onMsgChange(key, 'subject', e.target.value)}
                      sx={{ mb: 1.25 }}
                    />
                  )}
                  <TextField
                    fullWidth multiline minRows={4} label="Body"
                    value={m.body || ''}
                    onChange={(e) => onMsgChange(key, 'body', e.target.value)}
                  />
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* review decision */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>Review decision</Typography>
        {entry.reviewed_by_name && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Last reviewed by {entry.reviewed_by_name}
            {entry.reviewed_at ? ` on ${new Date(entry.reviewed_at).toLocaleDateString()}` : ''}
          </Typography>
        )}
        <TextField fullWidth multiline minRows={2} size="small" label="Notes (optional)"
          value={notes} onChange={e => setNotes(e.target.value)} sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {canApprove && (
            <Button variant="contained" color="success"
              startIcon={acting ? <CircularProgress size={14} /> : <Icon icon={checkmarkCircle2Fill} />}
              onClick={onApprove} disabled={acting}>
              Approve
            </Button>
          )}
          {canReject && (
            <Button variant="outlined" color="error"
              startIcon={<Icon icon={closeCircleFill} />}
              onClick={onReject} disabled={acting}>
              Reject
            </Button>
          )}
          {canRetry && (
            <Button variant="outlined"
              startIcon={<Icon icon={refreshFill} />}
              onClick={onRetry} disabled={acting}>
              Retry pipeline
            </Button>
          )}
          {entry.status === 'APPROVED' && (
            <Chip label="✓ Approved — queued for enrollment"
              sx={{ bgcolor: '#E9FCD4', color: '#229A16', fontWeight: 700 }} />
          )}
          {entry.status === 'ENROLLED' && (
            <Chip label="✓ Enrolled in campaign"
              sx={{ bgcolor: '#CAFDF5', color: '#006C9C', fontWeight: 700 }} />
          )}
          {entry.status === 'REJECTED' && (
            <Chip label="Rejected — can still be re-approved"
              sx={{ bgcolor: '#FFE7D9', color: '#B72136', fontWeight: 700 }} />
          )}
        </Stack>
      </Paper>

      {/* ── Preview dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={!!previewKey}
        onClose={() => setPreviewKey(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Preview — Step {previewDef?.step}: {previewDef?.label}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {previewDef?.day} · {STEP_COLORS[previewDef?.channel]?.label || previewDef?.channel}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {previewDef && previewMsg && (
            <>
              {/* Email-like header */}
              <Stack spacing={0.5} mb={2}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, fontWeight: 700 }}>From:</Typography>
                  <Typography variant="body2">{'{{pm_name}}'} (your Outlook account)</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, fontWeight: 700 }}>To:</Typography>
                  <Typography variant="body2">
                    {entry.contact_name || 'Contact'}{entry.contact_email ? ` <${entry.contact_email}>` : ''}
                  </Typography>
                </Stack>
                {previewDef.hasSubject && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, fontWeight: 700 }}>Subject:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{previewMsg.subject || '(no subject)'}</Typography>
                  </Stack>
                )}
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Rendered body + signature — composed identically to backend _compose_email_body.
                  Wrapper simulates a typical mail client (white card, sans-serif, body color) so
                  the WYSIWYG matches what the recipient will actually see. */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2.5,
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontSize: '14px',
                  color: '#202020',
                  lineHeight: 1.55,
                  overflowX: 'auto',
                  '& p': { m: 0, mb: '14px' },
                  '& p:last-child': { mb: 0 },
                  // Avoid MUI baseline color leakage into the rendered HTML
                  '& a':   { color: '#0a66c2' },
                  '& table': { borderCollapse: 'collapse' },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: composeEmailHtml(previewMsg.body || '', sigHtml) }} />
              </Box>
              {!sigHtml && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  No signature yet — fill the fields below and a TGC-branded signature will be appended automatically.
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Signature is auto-derived from the BA's account name — no form needed */}
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Signature sender:
                </Typography>
                <Chip
                  label={signatureMeta.pm_name || '(no name on your user)'}
                  size="small"
                  sx={{ height: 22, fontWeight: 700 }}
                />
                {signatureMeta.email && (
                  <Typography variant="caption" color="text.secondary">
                    · {signatureMeta.email}
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {!signatureMeta.loaded
                  ? 'Loading signature…'
                  : !signatureMeta.has_account
                  ? 'No active Outlook account on your user — connect one from /outreach/accounts before sending.'
                  : 'The TGC signature is generated automatically from the sending BA’s account name. The preview above uses your name; at send time each email uses the actual sending BA’s name.'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewKey(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ── CampaignQueue (main) ──────────────────────────────────────────────────────

const FILTER_TABS = [
  { value: '',              label: 'All' },
  { value: 'REVIEW',        label: 'Review' },
  { value: 'APPROVED',      label: 'Approved' },
  { value: 'ENROLLED',      label: 'Enrolled' },
  { value: 'DISQUALIFIED',  label: 'Disqualified' },
  { value: 'REJECTED',      label: 'Rejected' },
  { value: 'FAILED',        label: 'Failed' },
  { value: 'PENDING',       label: 'In pipeline' },
];

const CampaignQueue = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign]       = useState(null);
  const [stats, setStats]             = useState(null);
  const [entries, setEntries]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState('REVIEW');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [selectedId, setSelectedId]   = useState(null);
  const [msgs, setMsgs]               = useState({});
  const [msgsDirty, setMsgsDirty]     = useState(false);
  const [notes, setNotes]             = useState('');
  const [acting, setActing]           = useState(false);
  const [snack, setSnack]             = useState(null);
  const debounceRef = useRef(null);

  const selected = entries.find(e => e.id === selectedId) || null;

  // ── Grouping by company ────────────────────────────────────────────────
  const groupedByCompany = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.company)) {
        map.set(e.company, {
          company_id:       e.company,
          company_name:     e.company_name,
          company_country:  e.company_country,
          company_industry: e.company_industry,
          company_employees: e.company_employees,
          company_website:  e.company_website,
          contacts:         [],
        });
      }
      map.get(e.company).contacts.push(e);
    });
    return Array.from(map.values());
  }, [entries]);

  const [expanded, setExpanded] = useState(new Set());

  const toggleCompany = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Expand company of currently-selected contact, plus auto-expand when only one company in results
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (selectedId) {
        const sel = entries.find((e) => e.id === selectedId);
        if (sel) next.add(sel.company);
      }
      if (groupedByCompany.length === 1) {
        next.add(groupedByCompany[0].company_id);
      }
      return next;
    });
  }, [selectedId, entries, groupedByCompany.length]);

  const loadStats = useCallback(() =>
    getCampaignQueueStats(id).then(r => setStats(r.data)).catch(() => {}), [id]);

  const loadList = useCallback(() => {
    setLoading(true);
    const params = { limit: 150 };
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    return listCampaignQueue(id, params)
      .then(r => {
        const results = r.data.results || [];
        setEntries(results);
        setTotal(r.data.count || 0);
        if (results.length > 0 && !selectedId) {
          const first = results[0];
          setSelectedId(first.id);
          setMsgs(first.generated_messages || {});
          setNotes(first.reviewer_notes || '');
          setMsgsDirty(false);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, statusFilter, search]);

  useEffect(() => {
    getCampaign(id).then(r => setCampaign(r.data)).catch(() => {});
    loadStats();
  }, [id]);

  useEffect(() => { loadList(); }, [loadList]);

  // debounce search
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(e.target.value), 500);
  };

  const selectEntry = (entry) => {
    setSelectedId(entry.id);
    setMsgs(entry.generated_messages || {});
    setNotes(entry.reviewer_notes || '');
    setMsgsDirty(false);
  };

  const handleMsgChange = (key, field, value) => {
    setMsgs(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
    setMsgsDirty(true);
  };

  const saveMessages = async () => {
    if (!selectedId || !msgsDirty) return;
    await updateQueueEntryMessages(selectedId, msgs);
    setMsgsDirty(false);
    setEntries(prev => prev.map(e => e.id === selectedId ? { ...e, generated_messages: msgs } : e));
    setSnack({ severity: 'success', message: 'Messages saved.' });
  };

  const handleApprove = async () => {
    setActing(true);
    try {
      if (msgsDirty) await saveMessages();
      await approveQueueEntry(selectedId, { reviewer_notes: notes });
      setSnack({ severity: 'success', message: 'Approved — company queued for enrollment.' });
      setSelectedId(null);
      await loadStats();
      await loadList();
    } catch (err) {
      setSnack({ severity: 'error', message: err?.response?.data?.detail || 'Approve failed.' });
    } finally { setActing(false); }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await rejectQueueEntry(selectedId, { reviewer_notes: notes });
      setSnack({ severity: 'info', message: 'Company rejected.' });
      setSelectedId(null);
      await loadStats();
      await loadList();
    } catch (err) {
      setSnack({ severity: 'error', message: err?.response?.data?.detail || 'Reject failed.' });
    } finally { setActing(false); }
  };

  const handleRetry = async () => {
    setActing(true);
    try {
      await retryQueueEntry(selectedId);
      setSnack({ severity: 'info', message: 'Retry queued.' });
      await loadStats();
      await loadList();
    } catch (err) {
      setSnack({ severity: 'error', message: err?.response?.data?.detail || 'Retry failed.' });
    } finally { setActing(false); }
  };

  const tabLabel = (value) => {
    if (!stats) return value || 'All';
    if (!value) return `All (${stats.total ?? 0})`;
    const count = value === 'REVIEW'
      ? stats.review_ready
      : (value === 'PENDING' ? (
          (stats.by_status?.PENDING ?? 0) + (stats.by_status?.ENRICHING ?? 0) +
          (stats.by_status?.SCORING ?? 0) + (stats.by_status?.GENERATING ?? 0)
        ) : stats.by_status?.[value] ?? 0);
    return `${FILTER_TABS.find(t => t.value === value)?.label} (${count})`;
  };

  return (
    <Page title="Review Queue | CRM">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)', bgcolor: 'background.default' }}>

        {/* ── header ── */}
        <Box sx={{
          px: 2.5, py: 1.25, flexShrink: 0,
          borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Button size="small" startIcon={<Icon icon={arrowBackFill} />}
                  onClick={() => navigate(`/outreach/campaigns/${id}`)}>
            {campaign?.name || 'Campaign'}
          </Button>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Enrichment Review Queue
          </Typography>

          {/* Open the reviewer's card UI scoped to this campaign, across all BAs */}
          <Button size="small" variant="outlined"
                  onClick={() => navigate(`/my-outreach/review?campaign_id=${id}&all_pms=1`)}>
            Open review view (all BAs)
          </Button>

          {stats && (
            <Stack direction="row" spacing={2.5} sx={{ flexShrink: 0 }}>
              {[
                { label: 'Total',        value: stats.total },
                { label: 'Review',       value: stats.review_ready,                  color: 'warning.dark' },
                { label: 'Approved',     value: stats.by_status?.APPROVED ?? 0,      color: 'success.dark' },
                { label: 'Enrolled',     value: stats.by_status?.ENROLLED ?? 0,      color: 'info.main' },
                { label: 'Disqualified', value: stats.by_status?.DISQUALIFIED ?? 0,  color: 'text.secondary' },
                { label: 'Rejected',     value: stats.by_status?.REJECTED ?? 0,      color: 'error.main' },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
                    {value ?? '—'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* ── body ── */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>

          {/* left panel — list */}
          <Box sx={{
            width: 340, flexShrink: 0, borderRight: 1, borderColor: 'divider',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* search + filter chips */}
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <TextField
                size="small" fullWidth placeholder="Search company…"
                value={searchInput} onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Icon icon={searchFill} width={16} /></InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {FILTER_TABS.map(({ value }) => (
                  <Chip key={value || 'all'}
                    label={tabLabel(value)}
                    size="small"
                    variant={statusFilter === value ? 'filled' : 'outlined'}
                    color={statusFilter === value ? 'primary' : 'default'}
                    onClick={() => { setStatusFilter(value); setSelectedId(null); }}
                    sx={{ cursor: 'pointer', fontWeight: statusFilter === value ? 700 : 400 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* entries */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {loading && <LinearProgress />}
              {!loading && entries.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No companies match this filter.</Typography>
                </Box>
              )}
              {groupedByCompany.map((group) => {
                const isOpen = expanded.has(group.company_id);
                // Aggregate status — show the most actionable status badge on the company row
                const statusCounts = group.contacts.reduce((acc, c) => {
                  acc[c.status] = (acc[c.status] || 0) + 1;
                  return acc;
                }, {});
                const STATUS_PRIORITY = ['REVIEW', 'APPROVED', 'ENROLLED', 'PENDING',
                                          'ENRICHING', 'SCORING', 'GENERATING',
                                          'DISQUALIFIED', 'REJECTED', 'FAILED'];
                const headlineStatus = STATUS_PRIORITY.find((s) => statusCounts[s]);
                const headlineCount = headlineStatus ? statusCounts[headlineStatus] : 0;

                return (
                  <Box key={group.company_id}>
                    {/* Company header (clickable to expand/collapse) */}
                    <Box
                      onClick={() => toggleCompany(group.company_id)}
                      sx={{
                        px: 1.5, py: 1.25, cursor: 'pointer',
                        borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.neutral',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Icon icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill} width={16} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
                            {group.company_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {group.contacts.length} contact{group.contacts.length === 1 ? '' : 's'}
                            {group.company_country && ` · ${group.company_country}`}
                            {group.company_industry && ` · ${group.company_industry}`}
                          </Typography>
                        </Box>
                        {headlineStatus && (
                          <Box sx={{ flexShrink: 0 }}>
                            <Chip
                              label={`${headlineCount} ${(STATUS_META[headlineStatus]?.label || headlineStatus).toLowerCase()}`}
                              size="small"
                              sx={{
                                bgcolor: STATUS_META[headlineStatus]?.bg || '#F4F6F8',
                                color:   STATUS_META[headlineStatus]?.color || '#637381',
                                fontWeight: 700, fontSize: '0.65rem', height: 18, borderRadius: 1,
                              }}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>

                    {/* Contact rows (nested under company when expanded) */}
                    {isOpen && group.contacts.map((entry) => (
                      <Box
                        key={entry.id}
                        onClick={(ev) => { ev.stopPropagation(); selectEntry(entry); }}
                        sx={{
                          pl: 3.5, pr: 2, py: 1.25, cursor: 'pointer',
                          borderBottom: '1px solid', borderColor: 'divider',
                          borderLeft: selectedId === entry.id ? '3px solid' : '3px solid transparent',
                          borderLeftColor: selectedId === entry.id ? 'primary.main' : 'transparent',
                          bgcolor: selectedId === entry.id ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: selectedId === entry.id ? 'action.selected' : 'action.hover' },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                              {entry.contact_name || 'Unnamed contact'}
                            </Typography>
                            {entry.contact_title && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {entry.contact_title}
                              </Typography>
                            )}
                            {(entry.tier2_score != null || entry.tier3_score != null) && (
                              <Typography variant="caption" color="text.secondary">
                                T2 {entry.tier2_score ?? '—'}/5 · T3 {entry.tier3_score ?? '—'}/4
                              </Typography>
                            )}
                          </Box>
                          <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                            <StatusChipSmall status={entry.status} />
                            {entry.scoring_result && <ScoreBadge result={entry.scoring_result} />}
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                );
              })}
              {!loading && total > entries.length && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing {entries.length} of {total}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* right panel — detail */}
          <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2.5 }}>
            {!selected ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Select a company to review</Typography>
              </Box>
            ) : (
              <DetailPanel
                entry={selected}
                msgs={msgs}
                msgsDirty={msgsDirty}
                notes={notes}
                setNotes={setNotes}
                acting={acting}
                onMsgChange={handleMsgChange}
                onSaveMessages={saveMessages}
                onApprove={handleApprove}
                onReject={handleReject}
                onRetry={handleRetry}
              />
            )}
          </Box>
        </Box>

        <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)}>
          {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
        </Snackbar>
      </Box>
    </Page>
  );
};

export default CampaignQueue;
